import React, { useMemo, useState } from "react";
import { useUserRole } from "../../../context/UserRoleContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import { useBar } from "../../../context/BarContext";
import ShowCardModal from "../components/ShowCardModal";
import AddShowModal from "../components/AddShowModal";
import useBarArtists from "../hooks/useBarArtists";
import { getEffectiveSlotsForDate } from "../../../../lib/barTimeSlots";
import { formatDateForInput } from "../../../utils/calendarDate";
import {
  civilDateTodayInZone,
  normalizeBarTimeZone,
} from "../../../utils/civilDateTime.js";

const parseTimeToMinutes = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const minute = parseInt(minutes || "0", 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return hour * 60 + minute;
};

const doesShowMatchSlotExactly = (show, slot) => {
  return (
    parseTimeToMinutes(show.start_time) ===
      parseTimeToMinutes(slot.start_time) &&
    parseTimeToMinutes(show.end_time) === parseTimeToMinutes(slot.end_time)
  );
};

const getExactShowForSlot = (date, slot, showsForDay) => {
  return showsForDay.find((show) => doesShowMatchSlotExactly(show, slot));
};

const formatTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes}${ampm}`;
};

const Calendar = () => {
  const { userData, loading } = useUserRole();
  const { selectedBar, barShows, showsLoading, refreshBarShows } = useBar();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowCardModalOpen, setIsShowCardModalOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [isAddShowModalOpen, setIsAddShowModalOpen] = useState(false);
  const [addShowDefaults, setAddShowDefaults] = useState(() => ({
    date: civilDateTodayInZone(normalizeBarTimeZone(undefined)),
    timeSlotId: "",
    artistId: "",
  }));

  const getSlotsForDate = (date) => {
    if (!selectedBar?.time_slots) return [];
    const ymd = formatDateForInput(date);
    return getEffectiveSlotsForDate(selectedBar.time_slots, ymd);
  };

  const normalizedShows = useMemo(() => {
    if (!barShows?.length) return [];
    return barShows.map((show) => ({
      ...show,
      date: formatDateForInput(show.date),
    }));
  }, [barShows]);

  const showsByDate = useMemo(() => {
    if (!normalizedShows.length) return new Map();
    const map = new Map();

    normalizedShows.forEach((show) => {
      if (!map.has(show.date)) {
        map.set(show.date, []);
      }
      map.get(show.date).push(show);
    });

    map.forEach((list) => {
      list.sort((a, b) => {
        const aStart = parseTimeToMinutes(a.start_time) ?? 0;
        const bStart = parseTimeToMinutes(b.start_time) ?? 0;
        return aStart - bStart;
      });
    });

    return map;
  }, [normalizedShows]);

  const getShowsForDay = (date) => {
    if (!date) return [];
    const dateStr = formatDateForInput(date);
    return showsByDate.get(dateStr) ?? [];
  };

  const selectedDayShows = useMemo(
    () => (selectedDay ? getShowsForDay(selectedDay) : []),
    [selectedDay, showsByDate],
  );

  const { artists, loading: artistsLoading } = useBarArtists(
    selectedBar?.bar_id,
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!selectedBar) {
    return (
      <DashboardLayout>
        <div className="bar-owner-dashboard">
          <div className="no-bar-message">
            <h2>No Bar Selected</h2>
            <p>
              Please select a bar from the dropdown or add a new bar in
              Management.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Calendar navigation functions
  const moveMonthBackward = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const moveMonthForward = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  // Generate month dates with proper calendar grid (including leading/trailing days)
  const generateMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Get day of week (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDay.getDay();
    const lastDate = lastDay.getDate();

    const dates = [];

    // Add leading days from previous month
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      dates.push({ date, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDate; i++) {
      const date = new Date(year, month, i);
      dates.push({ date, isCurrentMonth: true });
    }

    // Add trailing days to complete the grid
    const remainingDays = 7 - (dates.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        dates.push({ date, isCurrentMonth: false });
      }
    }

    return dates;
  };

  const monthDates = generateMonthDates();
  const venueTodayYmd = civilDateTodayInZone(
    normalizeBarTimeZone(selectedBar.time_zone)
  );
  const isToday = (date) => formatDateForInput(date) === venueTodayYmd;

  // Get month and year display
  const getMonthDisplay = () => {
    return currentDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  // Handle day click
  const handleDayClick = (dateObj) => {
    if (!dateObj.isCurrentMonth) {
      return;
    }
    setSelectedDay(dateObj.date);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDay(null);
  };

  const openShowModal = (show, date, slot) => {
    if (!show) {
      openAddShowModal(date, slot?.id ?? "", "");
      return;
    }
    const fallbackDate =
      show.date || (date instanceof Date ? formatDateForInput(date) : "");
    setSelectedShow({
      ...show,
      date: fallbackDate,
    });
    setIsShowCardModalOpen(true);
  };

  const closeShowModal = () => {
    setIsShowCardModalOpen(false);
    setSelectedShow(null);
  };

  const openAddShowModal = (
    date = civilDateTodayInZone(
      normalizeBarTimeZone(selectedBar.time_zone)
    ),
    timeSlotId = "",
    artistId = "",
  ) => {
    const normalizedDate = formatDateForInput(date);
    setAddShowDefaults({
      date: normalizedDate,
      timeSlotId: timeSlotId ? String(timeSlotId) : "",
      artistId: artistId ? String(artistId) : "",
    });
    setIsAddShowModalOpen(true);
  };

  const closeAddShowModal = () => {
    setIsAddShowModalOpen(false);
    setAddShowDefaults({
      date: civilDateTodayInZone(
        normalizeBarTimeZone(selectedBar.time_zone)
      ),
      timeSlotId: "",
      artistId: "",
    });
  };

  return (
    <>
      <DashboardLayout>
        <div className="bar-owner-dashboard">
          {/* Calendar Section */}
          <div
            className="calendar-section"
            aria-busy={showsLoading ? "true" : "false"}
          >
            {showsLoading && (
              <div className="calendar-loading-overlay" role="status">
                <div className="calendar-loading-indicator">
                  <span
                    className="calendar-loading-spinner"
                    aria-hidden="true"
                  />
                  <span className="calendar-loading-text">
                    Refreshing schedule…
                  </span>
                </div>
              </div>
            )}
            {/* Calendar Navigation Buttons */}
            <div className="calendar-nav">
              <button
                type="button"
                className="nav-btn add-show-btn"
                onClick={() => openAddShowModal()}
              >
                + Add Show
              </button>
              <div className="calendar-nav-content">
                <div className="nav-btn-group">
                  <button
                    className="nav-btn month-btn"
                    onClick={moveMonthBackward}
                  >
                    ← Month
                  </button>
                </div>
                <div className="calendar-month-display">
                  {getMonthDisplay()}
                </div>
                <div className="nav-btn-group">
                  <button
                    className="nav-btn month-btn"
                    onClick={moveMonthForward}
                  >
                    Month →
                  </button>
                </div>
              </div>
            </div>

            {/* Weekday Headers */}
            <div className="calendar-weekday-headers">
              <div className="weekday-header">Sun</div>
              <div className="weekday-header">Mon</div>
              <div className="weekday-header">Tue</div>
              <div className="weekday-header">Wed</div>
              <div className="weekday-header">Thu</div>
              <div className="weekday-header">Fri</div>
              <div className="weekday-header">Sat</div>
            </div>

            {/* Month Calendar Grid */}
            <div className="month-calendar">
              {monthDates.map((dateObj, index) => {
                const showsForDay = dateObj.isCurrentMonth
                  ? getShowsForDay(dateObj.date)
                  : [];
                const showCount = showsForDay.length;
                const pendingCount = showsForDay.filter(
                  (s) => s.status === "pending",
                ).length;

                const shortDateLabel = dateObj.date.toLocaleDateString(
                  "en-US",
                  {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  },
                );

                const dayAriaLabel =
                  !dateObj.isCurrentMonth || showCount === 0
                    ? dateObj.isCurrentMonth
                      ? `${shortDateLabel}, no shows`
                      : undefined
                    : `${shortDateLabel}, ${showCount} show${
                        showCount !== 1 ? "s" : ""
                      }${pendingCount > 0 ? ", includes pending" : ""}. Open day details.`;

                return (
                <div
                  key={index}
                  className={`calendar-day-cell ${
                    isToday(dateObj.date) ? "today" : ""
                  } ${!dateObj.isCurrentMonth ? "other-month" : ""}`}
                  onClick={() => handleDayClick(dateObj)}
                  onKeyDown={(e) => {
                    if (!dateObj.isCurrentMonth) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDayClick(dateObj);
                    }
                  }}
                  role={dateObj.isCurrentMonth ? "button" : undefined}
                  tabIndex={dateObj.isCurrentMonth ? 0 : undefined}
                  aria-label={dayAriaLabel}
                >
                  <div className="day-header">
                    <div className="day-number">{dateObj.date.getDate()}</div>
                  </div>

                  {dateObj.isCurrentMonth && showCount > 0 && (
                    <div
                      className="calendar-day-mobile-summary"
                      aria-hidden="true"
                    >
                      <span className="calendar-day-has-dot" />
                      <span className="calendar-day-show-count">
                        {showCount}
                      </span>
                      {pendingCount > 0 && (
                        <span
                          className="calendar-day-pending-indicator"
                          title="Includes pending show"
                        />
                      )}
                    </div>
                  )}

                  {dateObj.isCurrentMonth && (
                    <div className="day-shows">
                      {(() => {
                        const matchedShowIds = new Set();
                        const timeSlots = getSlotsForDate(dateObj.date);

                        const slotCards = timeSlots.map((slot) => {
                          const show = getExactShowForSlot(
                            dateObj.date,
                            slot,
                            showsForDay,
                          );

                          if (show) {
                            const showKey =
                              show.show_id ??
                              `${show.date}-${show.start_time}-${
                                show.artist_id ?? ""
                              }`;
                            matchedShowIds.add(showKey);
                          }

                          return (
                            <div
                              key={`slot-${slot.id}-${formatDateForInput(
                                dateObj.date,
                              )}`}
                              className={`show-slot-compact ${slot.name.toLowerCase()} ${
                                show ? "has-show" : "no-show"
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={(event) => {
                                event.stopPropagation();
                                openShowModal(show, dateObj.date, slot);
                              }}
                              onKeyPress={(event) => {
                                if (
                                  event.key === "Enter" ||
                                  event.key === " "
                                ) {
                                  event.stopPropagation();
                                  openShowModal(show, dateObj.date, slot);
                                }
                              }}
                            >
                              <div className="slot-compact-info">
                                <span className="slot-label-compact">
                                  {slot.name}
                                </span>
                                {/* <span className="slot-time-compact">
                                {formatTime(slot.start_time)} -{" "}
                                {formatTime(slot.end_time)}
                              </span> */}
                              </div>
                              <span
                                className={`artist-name ${
                                  show && show.status === "pending"
                                    ? "pending"
                                    : ""
                                }`}
                              >
                                {show ? show.stage_name || "Artist" : "No show"}
                                {show && show.status === "pending" && (
                                  <span className="pending-badge">⏳</span>
                                )}
                              </span>
                            </div>
                          );
                        });

                        const offSlotShows = showsForDay.filter((show) => {
                          const showKey =
                            show.show_id ??
                            `${show.date}-${show.start_time}-${
                              show.artist_id ?? ""
                            }`;
                          return !matchedShowIds.has(showKey);
                        });

                        return (
                          <>
                            {slotCards}
                            {offSlotShows.map((show) => {
                              const showKey =
                                show.show_id ??
                                `${show.date}-${show.start_time}-${
                                  show.artist_id ?? ""
                                }`;
                              return (
                                <div
                                  key={`offslot-compact-${showKey}`}
                                  className="show-slot-compact has-show"
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openShowModal(show, dateObj.date, null);
                                  }}
                                  onKeyPress={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.stopPropagation();
                                      openShowModal(
                                        show,
                                        dateObj.date,
                                        null,
                                      );
                                    }
                                  }}
                                >
                                  <div className="slot-compact-info">
                                    <span className="slot-label-compact">
                                      {formatTime(show.start_time)} -{" "}
                                      {formatTime(show.end_time)}
                                    </span>
                                  </div>
                                  <span
                                    className={`artist-name ${
                                      show.status === "pending"
                                        ? "pending"
                                        : ""
                                    }`}
                                  >
                                    {show.stage_name || "Artist"}
                                    {show.status === "pending" && (
                                      <span className="pending-badge">⏳</span>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Modal */}
          {isModalOpen && selectedDay && (
            <>
              <div className="modal-overlay" onClick={closeModal}></div>
              <div className="modal-container calendar-day-detail-modal">
                <div className="modal-header">
                  <h2 className="modal-title">
                    {selectedDay.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </h2>
                  <button className="modal-close-btn" onClick={closeModal}>
                    ✕
                  </button>
                </div>

                <div className="modal-content">
                  {getSlotsForDate(selectedDay).map((slot) => {
                    const show = selectedDayShows.find((dayShow) =>
                      doesShowMatchSlotExactly(dayShow, slot),
                    );

                    return (
                      <div
                        key={`modal-slot-${slot.id}`}
                        className="modal-show-card"
                      >
                        <div
                          className={`modal-show-header ${slot.name.toLowerCase()}`}
                        >
                          <h3>{slot.name} Show</h3>
                          <span className="modal-show-time">
                            {formatTime(slot.start_time)} -{" "}
                            {formatTime(slot.end_time)}
                          </span>
                        </div>
                        <div className="modal-show-details">
                          <div className="detail-row">
                            <span className="detail-label">Artist:</span>
                            <span className="detail-value">
                              {show
                                ? show.stage_name || "Artist"
                                : "No show scheduled"}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Sales:</span>
                            <span className="detail-value">$0</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {(() => {
                    const daySlots = getSlotsForDate(selectedDay);
                    const offslotShows = selectedDayShows.filter(
                      (show) =>
                        !daySlots.some((slot) =>
                          doesShowMatchSlotExactly(show, slot),
                        ),
                    );

                    if (offslotShows.length === 0) {
                      return null;
                    }

                    return (
                      <>
                        {offslotShows.map((show) => {
                          const showKey =
                            show.show_id ??
                            `${show.date}-${show.start_time}-${
                              show.artist_id ?? ""
                            }`;
                          return (
                            <div
                              key={`modal-offslot-${showKey}`}
                              className="modal-show-card"
                            >
                              <div className="modal-show-header">
                                <h3>Custom times</h3>
                                <span className="modal-show-time">
                                  {formatTime(show.start_time)} -{" "}
                                  {formatTime(show.end_time)}
                                </span>
                              </div>
                              <div className="modal-show-details">
                                <div className="detail-row">
                                  <span className="detail-label">Artist:</span>
                                  <span className="detail-value">
                                    {show.stage_name || "Artist"}
                                  </span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Sales:</span>
                                  <span className="detail-value">$0</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      </DashboardLayout>
      <ShowCardModal
        isOpen={isShowCardModalOpen}
        onClose={closeShowModal}
        showId={selectedShow?.show_id}
        fallbackShow={selectedShow}
      />
      <AddShowModal
        isOpen={isAddShowModalOpen}
        onClose={closeAddShowModal}
        selectedBar={selectedBar}
        artists={artists}
        artistsLoading={artistsLoading}
        refreshBarShows={refreshBarShows}
        defaultDate={addShowDefaults.date}
        venueTimeZone={selectedBar.time_zone}
        defaultTimeSlotId={addShowDefaults.timeSlotId}
        defaultArtistId={addShowDefaults.artistId}
      />
    </>
  );
};

export default Calendar;
